import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LoadingSpinner from 'component/LoadingSpinner';
import MultiSelectDropdown from 'component/MultiSelectDropdown';
import TribeService from 'service/TribeService';
import ProjectService from 'service/ProjectService';
import DashboardDataService from 'service/DashboardDataService';
import UserDetailModal from 'component/userDetailModal/UserViewModal';
import {
  TribeCreateReq,
  TribeUpdateReq,
  AddProjectWithUsersReq,
  RemoveUserFromTribeProjectReq,
  DeleteProjectFromTribeReq
} from 'domain/payload/request/tribe/TribeReq';
import { DashboardConfigProjectResp } from 'domain/payload/response/dashboard/DashboardConfigProjectResp';
import { ProjectTaskConfigFieldUserResp } from 'domain/payload/response/project/ProjectTaskConfigResp';
import CustomDropdown, { DropdownOption } from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';

const getErrorMessage = (err: any, defaultMessage: string = 'Terjadi kesalahan'): string => {
  if (err?.response?.data?.message) {
    return err.response.data.message;
  }
  if (err?.message) {
    return err.message;
  }
  return defaultMessage;
};

const SIZE_OPTIONS: DropdownOption[] = [
  { id: 5, name: '5' },
  { id: 10, name: '10' },
  { id: 25, name: '25' },
  { id: 50, name: '50' },
];

const TribeForm: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);

  const navigate = useNavigate();
  const tribeService = TribeService();
  const projectService = ProjectService();
  const dashboardDataService = DashboardDataService();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [logo, setLogo] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const [projects, setProjects] = useState<any[]>([]);
  const [projectList, setProjectList] = useState<DashboardConfigProjectResp[]>([]);

  const [fieldUsers, setFieldUsers] = useState<ProjectTaskConfigFieldUserResp[]>([]);
  const [selectedProjectForAdd, setSelectedProjectForAdd] = useState<string>('');

  const [userInputMethod, setUserInputMethod] = useState<"selection" | "id" | "email">("selection");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [manualUserIds, setManualUserIds] = useState("");
  const [manualUserEmails, setManualUserEmails] = useState("");
  const [userSearch, setUserSearch] = useState("");

  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingAddProjectWithUsers, setIsLoadingAddProjectWithUsers] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isLoadingRemoveUser, setIsLoadingRemoveUser] = useState(false);
  const [isLoadingDeleteProject, setIsLoadingDeleteProject] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const [projectCurrentPage, setProjectCurrentPage] = useState(1);
  const [projectPageSize, setProjectPageSize] = useState(5);
  const [totalProjectPages, setTotalProjectPages] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const [projectSearch, setProjectSearch] = useState<string>('');
  const [tempProjectSearch, setTempProjectSearch] = useState<string>('');

  const [userSizes, setUserSizes] = useState<Record<string, number>>({});
  const [userSearches, setUserSearches] = useState<Record<string, string>>({});
  const [userCurrentPages, setUserCurrentPages] = useState<Record<string, number>>({});
  const [loadingUsers, setLoadingUsers] = useState<Record<string, boolean>>({});
  const DEFAULT_USER_SIZE = 5;

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const hasFetched = useRef(false);
  const hasFetchedUsers = useRef<Record<string, boolean>>({});
  const userSearchTimeouts = useRef<Record<string, NodeJS.Timeout | null>>({});
  const isFetchingUsers = useRef(false);

  const { navRef: inputMethodNavRef, indicatorStyle: inputMethodIndicatorStyle } = useNavPillsIndicator(userInputMethod);

  const handleViewUserDetail = (userId: string) => {
    setSelectedUserId(userId);
    setShowDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedUserId(null);
  };

  const fetchProjects = useCallback(async (searchValue?: string, page?: number) => {
    if (id === "0") return;
    const token = profile?.data.token;
    if (!token) return;

    const currentSearch = searchValue !== undefined ? searchValue : projectSearch;
    const currentPage = page !== undefined ? page : projectCurrentPage;

    setIsLoadingProjects(true);
    try {
      const res = await tribeService.getTribeProjects(
        token,
        id!,
        currentPage,
        projectPageSize,
        currentSearch || null
      );

      const projectsData = res.data || [];
      setProjects(projectsData);
      setTotalProjects(res.pagination?.totalRecords || 0);
      setTotalProjectPages(res.pagination?.totalPage || 1);

      hasFetchedUsers.current = {};

      projectsData.forEach((project: any) => {
        if (!userSizes[project.id]) {
          setUserSizes(prev => ({ ...prev, [project.id]: DEFAULT_USER_SIZE }));
        }
        if (!userCurrentPages[project.id]) {
          setUserCurrentPages(prev => ({ ...prev, [project.id]: 1 }));
        }
        if (!userSearches[project.id]) {
          setUserSearches(prev => ({ ...prev, [project.id]: '' }));
        }
      });
    } catch (err: any) {
      console.error(err);
      const errorMessage = getErrorMessage(err, 'Gagal mengambil data project');
      alert(errorMessage);
    } finally {
      setIsLoadingProjects(false);
    }
  }, [id, profile?.data.token, projectPageSize, projectSearch, projectCurrentPage]);

  const fetchProjectUsers = useCallback(async (projectId: string, page: number, size: number, search: string | null = null) => {
    const token = profile?.data.token;
    if (!token) return;

    setLoadingUsers(prev => ({ ...prev, [projectId]: true }));
    try {
      const res = await tribeService.getTribeUsers(
        token,
        id!,
        projectId,
        page,
        size,
        search || null
      );

      const usersData = res.data || [];
      const paginationData = res.pagination;

      setProjects(prevProjects =>
        prevProjects.map(project =>
          project.id === projectId
            ? {
              ...project,
              users: usersData,
              pagination: paginationData,
              totalUsers: paginationData?.totalRecords || 0
            }
            : project
        )
      );
    } catch (err: any) {
      console.error(err);
      const errorMessage = getErrorMessage(err, 'Gagal mengambil data user');
      alert(errorMessage);
    } finally {
      setLoadingUsers(prev => ({ ...prev, [projectId]: false }));
    }
  }, [id, profile?.data.token]);

  const fetchAvailableProjects = useCallback(async () => {
    const token = profile?.data.token;
    if (!token) return;

    setIsLoadingConfig(true);
    try {
      const resProject = await dashboardDataService.dashboardDataConfig(token);
      setProjectList(resProject.data?.projectList ?? []);
    } catch (err: any) {
      console.error(err);
    } finally {
      setIsLoadingConfig(false);
    }
  }, [profile?.data.token, dashboardDataService]);

  const fetchFieldUsers = useCallback(async () => {
    const token = profile?.data.token;
    if (!token) {
      setIsLoadingUsers(false);
      return;
    }

    if (isFetchingUsers.current) {
      return;
    }

    isFetchingUsers.current = true;
    setIsLoadingUsers(true);

    try {
      const searchParam = userSearch.trim() !== "" ? userSearch : null;
      const response = await projectService.projectTaskConfigFieldUser(
        token,
        searchParam,
        1,
        100
      );

      if (response && response.data && Array.isArray(response.data)) {
        setFieldUsers(response.data);
      } else {
        setFieldUsers([]);
      }
    } catch (err: any) {
      console.error("Error fetching users:", err);
      setFieldUsers([]);
    } finally {
      setIsLoadingUsers(false);
      isFetchingUsers.current = false;
    }
  }, [profile?.data.token, projectService, userSearch]);

  const fetchDetail = useCallback(async () => {
    const token = profile?.data.token;
    if (!token) return;

    setIsLoadingDetail(true);
    try {
      const res = await tribeService.getTribeDetail(token, id ?? "0");
      setName(res.data?.name || '');
      setDescription(res.data?.description || '');
      setEmail(res.data?.email || '');
      setPhone(res.data?.phone || '');
      setPreviewUrl(res.data?.logo || null);
    } catch (err: any) {
      console.error(err);
      const errorMessage = getErrorMessage(err, 'Gagal mengambil detail tribe');
      alert(errorMessage);
    } finally {
      setIsLoadingDetail(false);
    }
  }, [id, profile?.data.token]);

  const refreshData = useCallback(() => {
    hasFetchedUsers.current = {};
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (totalProjects > 0 && projectCurrentPage > totalProjectPages) {
      setProjectCurrentPage(totalProjectPages);
      fetchProjects(projectSearch, totalProjectPages);
    } else if (totalProjects === 0 && projectCurrentPage !== 1) {
      setProjectCurrentPage(1);
      fetchProjects(projectSearch, 1);
    }
  }, [totalProjects, totalProjectPages, projectCurrentPage, projectSearch, fetchProjects]);

  useEffect(() => {
    if (id !== "0" && !hasFetched.current) {
      hasFetched.current = true;
      Promise.all([
        fetchDetail(),
        fetchProjects(),
        fetchAvailableProjects()
      ]);
    }
  }, [id, fetchDetail, fetchProjects, fetchAvailableProjects]);

  useEffect(() => {
    if (id !== "0" && hasFetched.current && userInputMethod === "selection") {
      fetchFieldUsers();
    }
  }, [hasFetched.current]);

  useEffect(() => {
    if (!hasFetched.current) return;
    if (userInputMethod !== "selection") return;

    const timer = setTimeout(() => {
      fetchFieldUsers();
    }, 400);

    return () => clearTimeout(timer);
  }, [userSearch, userInputMethod]);

  useEffect(() => {
    if (!hasFetched.current) return;
    if (userInputMethod === "selection") {
      fetchFieldUsers();
    }
  }, [userInputMethod]);

  useEffect(() => {
    if (projects.length > 0 && hasFetched.current) {
      projects.forEach((project) => {
        if (!hasFetchedUsers.current[project.id]) {
          hasFetchedUsers.current[project.id] = true;
          const page = userCurrentPages[project.id] || 1;
          const size = userSizes[project.id] || DEFAULT_USER_SIZE;
          const search = userSearches[project.id] || '';
          fetchProjectUsers(project.id, page, size, search || null);
        }
      });
    }
  }, [projects, fetchProjectUsers, userCurrentPages, userSizes, userSearches]);

  const cleanInputIds = (input: string): string => {
    if (!input) return "";
    const parts = input.split(/[\n\r\t,]+/);
    const cleaned = parts
      .map(part => part.trim())
      .filter(part => part.length > 0);
    return cleaned.join(", ");
  };

  const cleanPastedText = (text: string): string => {
    if (!text) return "";
    const normalized = text
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\t/g, ' ');

    const parts = normalized.split(/[\n,;| ]+/);

    const cleaned = parts
      .map(part => part.trim())
      .filter(part => part.length > 0 && !part.match(/^-+$/));

    return cleaned.join(", ");
  };

  const handleUserInputMethodChange = (newMethod: "selection" | "id" | "email") => {
    if (newMethod === userInputMethod) return;

    if (userInputMethod === "selection" && newMethod === "email" && selectedUserIds.length > 0) {
      const selectedEmails = fieldUsers
        .filter(u => selectedUserIds.includes(u.id))
        .map(u => u.email)
        .filter(email => email !== null && email !== undefined && email.trim() !== "");

      if (selectedEmails.length > 0) {
        setManualUserEmails(selectedEmails.join(", "));
      } else {
        alert("User yang dipilih tidak memiliki email. Silakan pilih user dengan email yang valid.");
        return;
      }
    }

    if (userInputMethod === "selection" && newMethod === "id" && selectedUserIds.length > 0) {
      setManualUserIds(selectedUserIds.join(", "));
    }

    if (userInputMethod === "id" && newMethod === "selection" && manualUserIds.trim()) {
      const ids = manualUserIds.split(",").map(id => id.trim()).filter(Boolean);
      setSelectedUserIds(ids);
    }

    if (userInputMethod === "email" && newMethod === "selection" && manualUserEmails.trim()) {
      const emails = manualUserEmails.split(",").map(e => e.trim()).filter(Boolean);
      const matchingUsers = fieldUsers.filter(u =>
        u.email && emails.includes(u.email.trim())
      );
      setSelectedUserIds(matchingUsers.map(u => u.id));
    }

    if (userInputMethod === "email" && newMethod === "id" && manualUserEmails.trim()) {
      const emails = manualUserEmails.split(",").map(e => e.trim()).filter(Boolean);
      const matchingUserIds = fieldUsers
        .filter(u => u.email && emails.includes(u.email.trim()))
        .map(u => u.id);

      if (matchingUserIds.length > 0) {
        setManualUserIds(matchingUserIds.join(", "));
      }
    }

    if (userInputMethod === "id" && newMethod === "email" && manualUserIds.trim()) {
      const ids = manualUserIds.split(",").map(id => id.trim()).filter(Boolean);
      const matchingEmails = fieldUsers
        .filter(u => ids.includes(u.id))
        .map(u => u.email)
        .filter(email => email !== null && email !== undefined && email.trim() !== "");

      if (matchingEmails.length > 0) {
        setManualUserEmails(matchingEmails.join(", "));
      } else {
        alert("User ID yang dimasukkan tidak memiliki email. Silakan gunakan metode lain.");
        return;
      }
    }

    if (newMethod !== "id") {
      setManualUserIds("");
    }
    if (newMethod !== "email") {
      setManualUserEmails("");
    }
    if (newMethod !== "selection") {
      setSelectedUserIds([]);
    }

    setUserInputMethod(newMethod);
  };

  const getSelectedUsersCount = (): number => {
    if (userInputMethod === "selection") return selectedUserIds.length;
    if (userInputMethod === "id") return manualUserIds ? manualUserIds.split(",").length : 0;
    if (userInputMethod === "email") return manualUserEmails ? manualUserEmails.split(",").length : 0;
    return 0;
  };

  const handleAddProjectWithUsers = async () => {
    if (!selectedProjectForAdd) {
      alert("Pilih project terlebih dahulu");
      return;
    }

    let finalUserIds: string[] = [];

    if (userInputMethod === "selection") {
      finalUserIds = [...selectedUserIds];
    } else if (userInputMethod === "id") {
      const cleaned = cleanInputIds(manualUserIds);
      finalUserIds = cleaned ? cleaned.split(",").map(id => id.trim()) : [];
    } else if (userInputMethod === "email") {
      const cleaned = cleanInputIds(manualUserEmails);
      const emails = cleaned ? cleaned.split(",").map(email => email.trim()) : [];

      for (const email of emails) {
        const user = fieldUsers.find(u => u.email === email);
        if (user) {
          finalUserIds.push(user.id);
        } else {
          alert(`User dengan email "${email}" tidak ditemukan`);
          return;
        }
      }
    }

    if (finalUserIds.length === 0) {
      alert("Pilih minimal satu user");
      return;
    }

    const token = profile?.data.token;
    if (!token) return;

    setIsLoadingAddProjectWithUsers(true);
    try {
      const req: AddProjectWithUsersReq = {
        tribeId: id!,
        projectId: selectedProjectForAdd,
        userIds: finalUserIds
      };
      await tribeService.addProjectWithUsers(token, req);

      alert(`Berhasil menambahkan ${finalUserIds.length} user ke project`);
      setSelectedProjectForAdd('');
      if (userInputMethod === "selection") {
        setSelectedUserIds([]);
      } else if (userInputMethod === "id") {
        setManualUserIds("");
      } else if (userInputMethod === "email") {
        setManualUserEmails("");
      }
      refreshData();
    } catch (err: any) {
      console.error(err);
      const errorMessage = getErrorMessage(err, 'Gagal menambahkan user ke project');
      alert(errorMessage);
    } finally {
      setIsLoadingAddProjectWithUsers(false);
    }
  };

  const handleRemoveUser = async (userId: string, projectId: string) => {
    if (!window.confirm("Hapus user ini dari project?")) return;

    const token = profile?.data.token;
    if (!token) return;

    setIsLoadingRemoveUser(true);
    try {
      const req: RemoveUserFromTribeProjectReq = {
        tribeId: id!,
        projectId: projectId,
        userId: userId
      };
      await tribeService.removeUserFromTribeProject(token, req);
      alert("User berhasil dihapus");
      refreshData();
    } catch (err: any) {
      console.error(err);
      const errorMessage = getErrorMessage(err, 'Gagal menghapus user');
      alert(errorMessage);
    } finally {
      setIsLoadingRemoveUser(false);
    }
  };

  const handleDeleteProject = async (projectId: string, projectName: string) => {
    if (!window.confirm(`Hapus seluruh project "${projectName}" dan semua user di dalamnya?`)) return;

    const token = profile?.data.token;
    if (!token) return;

    setIsLoadingDeleteProject(true);
    try {
      const req: DeleteProjectFromTribeReq = {
        tribeId: id!,
        projectId: projectId
      };
      await tribeService.deleteProjectFromTribe(token, req);
      alert(`Project berhasil dihapus`);
      refreshData();
    } catch (err: any) {
      console.error(err);
      const errorMessage = getErrorMessage(err, 'Gagal menghapus project');
      alert(errorMessage);
    } finally {
      setIsLoadingDeleteProject(false);
    }
  };

  const handleProjectPageChange = (newPage: number) => {
    setProjectCurrentPage(newPage);
    fetchProjects(projectSearch, newPage);
  };

  const handleProjectSizeChange = (newSize: number) => {
    setProjectPageSize(newSize);
    setProjectCurrentPage(1);
    setTimeout(() => {
      fetchProjects(projectSearch, 1);
    }, 0);
  };

  const handleSearchClick = () => {
    setProjectSearch(tempProjectSearch);
    setProjectCurrentPage(1);
    hasFetchedUsers.current = {};
    fetchProjects(tempProjectSearch, 1);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setTempProjectSearch(newValue);

    if (newValue === '') {
      setProjectSearch('');
      setProjectCurrentPage(1);
      hasFetchedUsers.current = {};
      fetchProjects('', 1);
    }
  };

  const handleUserPageChange = (projectId: string, newPage: number) => {
    setUserCurrentPages(prev => ({ ...prev, [projectId]: newPage }));
    const currentSize = userSizes[projectId] || DEFAULT_USER_SIZE;
    const currentSearch = userSearches[projectId] || '';
    fetchProjectUsers(projectId, newPage, currentSize, currentSearch || null);
  };

  const handleUserSizeChange = (projectId: string, newSize: number) => {
    setUserSizes(prev => ({ ...prev, [projectId]: newSize }));
    setUserCurrentPages(prev => ({ ...prev, [projectId]: 1 }));
    const currentSearch = userSearches[projectId] || '';
    fetchProjectUsers(projectId, 1, newSize, currentSearch || null);
  };

  const handleUserSearchChange = (projectId: string, searchValue: string) => {
    setUserSearches(prev => ({ ...prev, [projectId]: searchValue }));

    if (userSearchTimeouts.current[projectId]) {
      clearTimeout(userSearchTimeouts.current[projectId]!);
    }

    userSearchTimeouts.current[projectId] = setTimeout(() => {
      setUserCurrentPages(prev => ({ ...prev, [projectId]: 1 }));
      const currentSize = userSizes[projectId] || DEFAULT_USER_SIZE;
      fetchProjectUsers(projectId, 1, currentSize, searchValue || null);
    }, 500);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoadingSave(true);
    try {
      let tribeId: string | undefined = id;

      if (id === "0") {
        const result = await tribeCreate();
        tribeId = result?.data?.id;
        if (tribeId) {
          if (logo) {
            const token = profile?.data.token;
            if (token) {
              await tribeService.uploadTribeLogo(token, tribeId, logo);
            }
          }
          alert(`Data ${name} berhasil dibuat!`);
          navigate(`/tribe/${tribeId}`, { replace: true });
          window.location.reload();
          return;
        }
      } else {
        await tribeUpdate();
        if (logo) {
          const token = profile?.data.token;
          if (token) {
            await tribeService.uploadTribeLogo(token, id!, logo);
          }
        }
        alert(`Data ${name} berhasil diperbarui!`);
        navigate("/tribe");
      }
    } catch (err: any) {
      console.error(err);
      const errorMessage = getErrorMessage(err, 'Gagal menyimpan data');
      alert(errorMessage);
    } finally {
      setIsLoadingSave(false);
    }
  };

  const tribeCreate = async () => {
    const token = profile?.data.token;
    if (!token) throw new Error('No token');

    const request: TribeCreateReq = {
      name: name || null,
      description: description || null,
      email: email || null,
      phone: phone || null,
    };
    return await tribeService.createTribe(token, request);
  };

  const tribeUpdate = async () => {
    const token = profile?.data.token;
    if (!token) throw new Error('No token');

    const request: TribeUpdateReq = {
      id: id!,
      name: name || null,
      description: description || null,
      email: email || null,
      phone: phone || null,
    };
    await tribeService.updateTribe(token, request);
  };

  const MAX_SIZE = 100 * 1024;

  const handleFileChange = (file: File | null) => {
    if (!file) {
      setLogo(null);
      setPreviewUrl(null);
      return;
    }

    if (file.type !== "image/png") {
      alert("Format gambar harus .png");
      return;
    }
    if (file.size > MAX_SIZE) {
      alert("Ukuran file maksimal 100 KB");
      return;
    }
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      if (img.width !== img.height) {
        alert("Gambar harus rasio 1:1");
        URL.revokeObjectURL(objectUrl);
        return;
      }

      setLogo(file);
      setPreviewUrl(objectUrl);
    };

    img.src = objectUrl;
  };

  const allProjectOptions = projectList.map(p => ({
    code: p.id,
    name: p.name
  }));

  const projectsAlreadyAddedIds = projects.map(p => p.id);
  const projectOptionsWithIndicator = allProjectOptions.map(p => ({
    code: p.code,
    name: projectsAlreadyAddedIds.includes(p.code) ? `${p.name}` : p.name
  }));

  const availableUserOptions = fieldUsers.map(user => ({
    code: user.id,
    name: user.name,
    description: `${user.email || '-'} | ${user.phone || '-'}`
  }));

  useEffect(() => {
    if (userInputMethod === "selection") {
      setSelectedUserIds([]);
    } else if (userInputMethod === "id") {
      setManualUserIds("");
    } else if (userInputMethod === "email") {
      setManualUserEmails("");
    }
  }, [selectedProjectForAdd]);

  const startProjectRecord = (projectCurrentPage - 1) * projectPageSize + 1;
  const endProjectRecord = Math.min(projectCurrentPage * projectPageSize, totalProjects);

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4">{id === "0" ? "Add Tribe" : "Edit Tribe"}</h4>
        <div className="p-4" style={{ background: 'white' }}>
          {isLoadingDetail ? <LoadingSpinner /> :
            <>
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-sm-6">
                    <h5 className="mb-4">Basic Information</h5>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Name</label>
                      <input
                        type="text"
                        className="form-control form-control-compact"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Description</label>
                      <textarea
                        className="form-control form-control-compact"
                        rows={3}
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <h5 className="mb-4">Contact Information & Logo</h5>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Email</label>
                      <input
                        type="email"
                        className="form-control form-control-compact"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Phone</label>
                      <input
                        type="tel"
                        className="form-control form-control-compact"
                        value={phone}
                        onChange={e => setPhone(e.target.value)}
                      />
                    </div>
                    <div className='mb-3'>
                      <label className="form-label fw-semibold">Logo</label>
                      <div className='d-flex gap-3'>
                        {previewUrl ?
                          <img
                            src={previewUrl}
                            className='rounded col-auto border'
                            alt="Logo Preview"
                            style={{ width: '120px', height: '120px', objectFit: 'contain' }}
                          />
                          :
                          <span className='rounded col-auto border text-center d-flex align-items-center justify-content-center' style={{ width: '120px', height: '120px' }}>
                            Belum Ada Logo
                          </span>
                        }
                        <div className='col'>
                          <input
                            type="file"
                            className="form-control form-control-compact"
                            accept="image/png"
                            onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                          />
                          <small className="text-muted">* Format PNG, maksimal 100KB, rasio 1:1</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-100 d-flex justify-content-end mt-3 gap-2">
                  <Link to="/tribe">
                    <button type="button" className="btn btn-outline-primary">Cancel</button>
                  </Link>
                  <button type="submit" className="btn btn-primary" disabled={isLoadingSave}>
                    {isLoadingSave && <LoadingSpinner reverse />}
                    Save
                  </button>
                </div>
              </form>

              {id !== "0" && (
                <>
                  <hr className="my-4" />
                  <h5 className="mb-4 fw-bold">Project & User Management</h5>

                  <div className="row g-4 mb-4">
                    <div className="col-12">
                      <div className="card border-0">
                        <div className="card-body py-3">
                          <div className="row align-items-center">
                            <div className="col-md-3">
                              <label className="fw-semibold mb-2 mb-md-0">Project</label>
                            </div>
                            <div className="col-md-9">
                              <MultiSelectDropdown
                                options={projectOptionsWithIndicator}
                                placeholder="Choose Project..."
                                onChange={(e) => setSelectedProjectForAdd(e[0])}
                                single={true}
                                isSourceModal={true}
                                value={selectedProjectForAdd ? [selectedProjectForAdd] : []}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="card border-0">
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-3">
                              <label className="fw-semibold mb-2 mb-md-0">Users</label>
                            </div>
                            <div className="col-md-9">
                              <div className="d-flex gap-2 mb-3 flex-wrap">
                                <div ref={inputMethodNavRef} className="nav-pills-animated h-100" style={{ height: 50, maxHeight: 50 }}>
                                  <div
                                    className="nav-pill-indicator"
                                    style={inputMethodIndicatorStyle}
                                  />
                                  <button
                                    className={`nav-link btn-sm ${userInputMethod === "selection" ? "active" : ""}`}
                                    onClick={() => handleUserInputMethodChange("selection")}
                                    type="button"
                                  >
                                    Selection
                                  </button>
                                  <button
                                    className={`nav-link btn-sm ${userInputMethod === "id" ? "active" : ""}`}
                                    onClick={() => handleUserInputMethodChange("id")}
                                    type="button"
                                  >
                                    Input ID
                                  </button>
                                  <button
                                    className={`nav-link btn-sm ${userInputMethod === "email" ? "active" : ""}`}
                                    onClick={() => handleUserInputMethodChange("email")}
                                    type="button"
                                  >
                                    Input Email
                                  </button>
                                </div>
                              </div>

                              {userInputMethod === "selection" ? (
                                <div style={{ position: "relative" }}>
                                  <MultiSelectDropdown
                                    options={availableUserOptions}
                                    value={selectedUserIds}
                                    placeholder="Select Users"
                                    onChange={setSelectedUserIds}
                                    onSearchChange={(value) => setUserSearch(value)}
                                    previewKey="name"
                                    subKey="description"
                                    stayOpenOnUpdate
                                  />
                                  {isLoadingUsers && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: "rgba(255,255,255,0.7)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: "0.25rem",
                                        zIndex: 10,
                                      }}
                                    >
                                      <div className="spinner-border spinner-border-sm text-primary"></div>
                                      <span className="ms-2 text-primary">Loading users...</span>
                                    </div>
                                  )}
                                </div>
                              ) : userInputMethod === "id" ? (
                                <textarea
                                  className="form-control"
                                  rows={3}
                                  placeholder="user-1, user-2"
                                  value={manualUserIds}
                                  onChange={(e) => setManualUserIds(e.target.value)}
                                  onPaste={(e) => {
                                    e.preventDefault();
                                    const pastedText = e.clipboardData.getData('text');
                                    setManualUserIds(cleanPastedText(pastedText));
                                  }}
                                  onBlur={(e) => {
                                    if (e.target.value.trim()) {
                                      setManualUserIds(cleanInputIds(e.target.value));
                                    }
                                  }}
                                />
                              ) : (
                                <textarea
                                  className="form-control"
                                  rows={3}
                                  placeholder="email1@example.com, email2@example.com"
                                  value={manualUserEmails}
                                  onChange={(e) => setManualUserEmails(e.target.value)}
                                  onPaste={(e) => {
                                    e.preventDefault();
                                    const pastedText = e.clipboardData.getData('text');
                                    setManualUserEmails(cleanPastedText(pastedText));
                                  }}
                                  onBlur={(e) => {
                                    if (e.target.value.trim()) {
                                      setManualUserEmails(cleanInputIds(e.target.value));
                                    }
                                  }}
                                />
                              )}
                              <div className="mt-2 text-muted small">
                                Selected: {userInputMethod === "selection" ? selectedUserIds.length : userInputMethod === "id" ? (manualUserIds ? manualUserIds.split(",").length : 0) : (manualUserEmails ? manualUserEmails.split(",").length : 0)} user(s)
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="d-flex justify-content-end">
                        <button
                          className="btn btn-primary px-5 py-2"
                          type="button"
                          onClick={handleAddProjectWithUsers}
                          disabled={isLoadingAddProjectWithUsers || !selectedProjectForAdd || getSelectedUsersCount() === 0}
                          style={{ minWidth: "180px" }}
                        >
                          {isLoadingAddProjectWithUsers ? (
                            <>
                              <LoadingSpinner reverse />
                              <span className="ms-2">Adding...</span>
                            </>
                          ) : (
                            "Add to Tribe"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <hr className="my-4" />

                  <div className="card border-0">
                    <div className="card-body py-3">
                      <div className="row">
                        <div className="col-md-5">
                          <div className="d-flex gap-2">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search project"
                              value={tempProjectSearch}
                              onChange={handleSearchInputChange}
                              onKeyPress={handleSearchKeyPress}
                              style={{ height: "50px" }}
                            />
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={handleSearchClick}
                              disabled={isLoadingProjects}
                              style={{ height: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}
                            >
                              {isLoadingProjects ? <LoadingSpinner reverse /> : <i className="fa-solid fa-search"></i>}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card border-0">
                    <div className="card-header bg-white border-bottom-0 pt-4 px-4">
                      <div className="d-flex align-items-center">
                        <h6 className="mb-0 fw-semibold">Projects & Users List</h6>
                      </div>
                    </div>
                    <div className="card-body p-4 pt-0">
                      {isLoadingProjects ? (
                        <div className="text-center py-5"><LoadingSpinner /></div>
                      ) : projects.length > 0 ? (
                        <>
                          {projects.map((project) => {
                            const currentUserPage = userCurrentPages[project.id] || 1;
                            const totalUserPages = project.pagination?.totalPage || 1;
                            const totalUsers = project.totalUsers || 0;
                            const currentUserSize = userSizes[project.id] || DEFAULT_USER_SIZE;
                            const currentUserSearch = userSearches[project.id] || '';
                            const isLoadingUsersForProject = loadingUsers[project.id] || false;

                            const startUserRecord = (currentUserPage - 1) * currentUserSize + 1;
                            const endUserRecord = Math.min(currentUserPage * currentUserSize, totalUsers);

                            return (
                              <div key={project.id} className="mb-4 border rounded-3 p-3">
                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                  <h6 className="mb-0 fw-semibold">{project.name}</h6>
                                  <button
                                    className="btn btn-outline-danger btn-sm"
                                    type="button"
                                    onClick={() => handleDeleteProject(project.id, project.name)}
                                    disabled={isLoadingDeleteProject}
                                  >
                                    {isLoadingDeleteProject ? <LoadingSpinner reverse /> : <><i className="fa-solid fa-trash me-1"></i> Remove Project</>}
                                  </button>
                                </div>

                                <div className="mb-3">
                                  <div className="row">
                                    <div className="col-md-4">
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Search user..."
                                        value={currentUserSearch}
                                        onChange={e => handleUserSearchChange(project.id, e.target.value)}
                                      />
                                    </div>
                                  </div>
                                </div>

                                {isLoadingUsersForProject ? (
                                  <div className="text-center py-3"><LoadingSpinner /></div>
                                ) : project.users && project.users.length > 0 ? (
                                  <>
                                    <div className="table-responsive">
                                      <table className="table table-condensed table-hover table-sm">
                                        <thead className="table-primary">
                                          <tr>
                                            <th className="text-start">Name</th>
                                            <th className="text-start">Email</th>
                                            <th className="text-start">Phone</th>
                                            <th></th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {project.users.map((user: any) => (
                                            <tr key={user.id}>
                                              <td className="text-start">{user.name}</td>
                                              <td className="text-start">{user.email || '-'}</td>
                                              <td className="text-start">{user.phone || '-'}</td>
                                              <td className="text-center">
                                                <div className="d-flex justify-content-center gap-1">
                                                  <button
                                                    className="border-0 btn btn-outline-info btn-small-compact d-flex align-items-center justify-content-center"
                                                    onClick={() => handleViewUserDetail(user.id)}
                                                    title="View Details"
                                                    style={{ width: '28px', height: '28px' }}
                                                  >
                                                    <i className="fa-solid fa-eye"></i>
                                                  </button>
                                                  <button
                                                    className="border-0 btn btn-outline-danger btn-small-compact"
                                                    type="button"
                                                    onClick={() => handleRemoveUser(user.id, project.id)}
                                                    disabled={isLoadingRemoveUser}
                                                    title="Remove user from this project"
                                                  >
                                                    <i className="fa-solid fa-trash"></i>
                                                  </button>
                                                </div>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>

                                    {totalUserPages > 1 && (
                                      <div className="mt-3 d-flex justify-content-end align-items-center">
                                        <div className="d-flex align-items-center">
                                          <div className="me-4 d-flex align-items-center">
                                            <label className="me-2">Rows per page:</label>
                                            <CustomDropdown
                                              value={currentUserSize}
                                              options={SIZE_OPTIONS}
                                              onChange={(opt) => handleUserSizeChange(project.id, Number(opt.id))}
                                              align="top"
                                            />
                                          </div>
                                          <span className="me-3">
                                            {totalUsers > 0 ? `${startUserRecord}–${endUserRecord} of ${totalUsers}` : `0 of 0`}
                                          </span>
                                          <nav>
                                            <ul className="mb-0 pagination">
                                              <li className={`page-item ${currentUserPage === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => handleUserPageChange(project.id, 1)} style={{ border: 0 }}>
                                                  <i className="fa fa-angle-double-left"></i>
                                                </button>
                                              </li>
                                              <li className={`page-item ${currentUserPage === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => handleUserPageChange(project.id, currentUserPage - 1)} style={{ border: 0 }}>
                                                  <i className="fa fa-angle-left"></i>
                                                </button>
                                              </li>
                                              <li className="page-item disabled">
                                                <span className="page-link" style={{ border: 0 }}>{currentUserPage}</span>
                                              </li>
                                              <li className={`page-item ${currentUserPage === totalUserPages ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => handleUserPageChange(project.id, currentUserPage + 1)} style={{ border: 0 }}>
                                                  <i className="fa fa-angle-right"></i>
                                                </button>
                                              </li>
                                              <li className={`page-item ${currentUserPage === totalUserPages ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => handleUserPageChange(project.id, totalUserPages)} style={{ border: 0 }}>
                                                  <i className="fa fa-angle-double-right"></i>
                                                </button>
                                              </li>
                                            </ul>
                                          </nav>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-center py-3 text-muted small">
                                    No users found in this project.
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {totalProjectPages > 1 && (
                            <div className="mt-3 d-flex justify-content-end align-items-center">
                              <div className="d-flex align-items-center">
                                <div className="me-4">
                                  <label className="me-2">Projects per page:</label>
                                  <select
                                    className="w-auto form-select d-inline-block"
                                    value={projectPageSize}
                                    onChange={(e) => handleProjectSizeChange(parseInt(e.target.value))}
                                    style={{ width: 'auto' }}
                                  >
                                    {[5, 10, 25, 50].map(size => (
                                      <option key={size} value={size}>{size}</option>
                                    ))}
                                  </select>
                                </div>
                                <span className="me-3">
                                  {totalProjects > 0 ? `${startProjectRecord}–${endProjectRecord} of ${totalProjects}` : `0 of 0`}
                                </span>
                                <nav>
                                  <ul className="mb-0 pagination">
                                    <li className={`page-item ${projectCurrentPage === 1 ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => handleProjectPageChange(1)} style={{ border: 0 }}>
                                        <i className="fa fa-angle-double-left"></i>
                                      </button>
                                    </li>
                                    <li className={`page-item ${projectCurrentPage === 1 ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => handleProjectPageChange(projectCurrentPage - 1)} style={{ border: 0 }}>
                                        <i className="fa fa-angle-left"></i>
                                      </button>
                                    </li>
                                    <li className="page-item disabled">
                                      <span className="page-link" style={{ border: 0 }}>{projectCurrentPage}</span>
                                    </li>
                                    <li className={`page-item ${projectCurrentPage === totalProjectPages ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => handleProjectPageChange(projectCurrentPage + 1)} style={{ border: 0 }}>
                                        <i className="fa fa-angle-right"></i>
                                      </button>
                                    </li>
                                    <li className={`page-item ${projectCurrentPage === totalProjectPages ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={() => handleProjectPageChange(totalProjectPages)} style={{ border: 0 }}>
                                        <i className="fa fa-angle-double-right"></i>
                                      </button>
                                    </li>
                                  </ul>
                                </nav>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-5 text-muted">
                          No projects added yet. Select a project above and add users.
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          }
        </div>
      </div>

      <UserDetailModal
        show={showDetailModal}
        onClose={handleCloseModal}
        userId={selectedUserId || undefined}
        token={profile?.data.token}
      />
    </div>
  );
};

export default TribeForm;